import { useEffect, type RefObject } from 'react'

const FOCUSABILE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Ține focusul tastaturii în interiorul unui dialog modal cât e deschis și îl readuce, la
 * închidere, pe elementul care îl deschisese. Fără asta, Tab-ul ieșea din modal în pagina
 * de dedesubt (acoperită vizual), iar după închidere focusul se pierdea pe <body>.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, activ: boolean) {
  useEffect(() => {
    if (!activ) return
    const anterior = document.activeElement as HTMLElement | null
    const container = ref.current
    if (container && !container.contains(document.activeElement)) {
      const primul = container.querySelector<HTMLElement>(FOCUSABILE)
      ;(primul ?? container).focus({ preventScroll: true })
    }

    const laTasta = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !ref.current) return
      const elemente = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABILE))
        .filter(el => el.offsetParent !== null || el === document.activeElement)
      if (!elemente.length) { e.preventDefault(); return }
      const primul = elemente[0]
      const ultimul = elemente[elemente.length - 1]
      const curent = document.activeElement as HTMLElement | null
      if (e.shiftKey && (curent === primul || !ref.current.contains(curent))) {
        e.preventDefault(); ultimul.focus()
      } else if (!e.shiftKey && (curent === ultimul || !ref.current.contains(curent))) {
        e.preventDefault(); primul.focus()
      }
    }
    document.addEventListener('keydown', laTasta)
    return () => {
      document.removeEventListener('keydown', laTasta)
      if (anterior && document.contains(anterior)) anterior.focus({ preventScroll: true })
    }
  }, [ref, activ])
}
